import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ShieldCheck, SearchCheck, XCircle, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BatchVerify() {
  const [lot, setLot] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // null | {found:bool,...}

  const verify = async (e) => {
    e.preventDefault();
    const key = lot.trim();
    if (!key) return;
    setLoading(true); setResult(null);
    try {
      const { data } = await axios.get(`${API}/batches/verify`, { params: { lot: key } });
      setResult(data);
    } catch (err) { setResult({ error: true }); }
    finally { setLoading(false); }
  };

  return (
    <main className="pt-32 pb-28" data-testid="batch-verify-page">
      <Seo title="Verify a Batch — Documentation Lookup" path="/verify"
        description="Verify a Molecularix Biolabs batch or lot number to view the documentation on record. Results are database-backed and never fabricated." />
      <div className="max-w-2xl mx-auto px-6 md:px-12">
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-5">Documentation</motion.p>
        <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-light leading-[1.02] mb-5">Verify a Batch</h1>
        <p className="text-[#A1A1AA] text-base leading-relaxed mb-10">
          Enter a batch or lot number to view the documentation on record. Results are drawn directly from our records — if a batch isn&apos;t on file yet, we&apos;ll tell you clearly.
        </p>

        <form onSubmit={verify} className="flex flex-col sm:flex-row gap-3 mb-10">
          <input value={lot} onChange={(e) => setLot(e.target.value)} data-testid="batch-input"
            aria-label="Batch or lot number" placeholder="Batch / Lot number"
            className="flex-1 bg-transparent border border-zinc-800 focus:border-[#D4AF37] outline-none px-4 py-3.5 font-mono text-sm placeholder:text-[#52525B] transition-colors" />
          <button type="submit" disabled={loading || !lot.trim()} data-testid="batch-verify-btn"
            className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#C5A028] transition-colors disabled:opacity-40">
            <SearchCheck size={15} /> {loading ? "Checking…" : "Verify"}
          </button>
        </form>

        {result && result.found && (
          <div className="border border-[#D4AF37]/40 bg-[#D4AF37]/[0.05] p-8" data-testid="batch-result-found">
            <div className="flex items-center gap-2 mb-6">
              <ShieldCheck size={18} className="text-[#D4AF37]" />
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Batch verified</span>
            </div>
            <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
              <Field label="Compound" value={result.compound} />
              <Field label="Batch / Lot" value={result.lot} />
              <Field label="Identity" value={result.identity} />
              <Field label="Purity" value={result.purity} />
              <Field label="Date" value={result.date} />
            </dl>
          </div>
        )}

        {result && !result.found && !result.error && (
          <div className="border border-zinc-800 bg-[#0C0C0C] p-8 text-center" data-testid="batch-result-none">
            <XCircle size={26} className="text-[#71717A] mx-auto mb-3" />
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">No record found</p>
            <p className="text-[#71717A] text-sm mb-6">We don&apos;t have a documentation record on file for batch &ldquo;{result.lot}&rdquo;. It may not be published yet — request documentation and we&apos;ll follow up.</p>
            <Link to="/documentation" data-testid="batch-request-docs"
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#C5A028] transition-colors">
              Request documentation <ArrowRight size={13} />
            </Link>
          </div>
        )}

        {result && result.error && (
          <div className="border border-red-400/30 bg-red-400/[0.04] p-6 text-center" data-testid="batch-result-error">
            <p className="text-[#A1A1AA] text-sm">Something went wrong checking that batch. Please try again in a moment.</p>
          </div>
        )}
      </div>
    </main>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <dt className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#71717A] mb-1">{label}</dt>
      <dd className="font-serif text-lg text-[#F5F5F0]">{value || "\u2014"}</dd>
    </div>
  );
}
